import { type Calendar } from '@dl/calendar';
import type { FactValidity, Precision } from '@dl/shared';
import { div, input, label, span, type Mountable } from '@llui/dom';

import { type CalendarGranularity } from './atoms/calendar-date-picker.js';
import { selectInput } from './atoms/select-input.js';
import {
  calendarInput,
  DEFAULT_CALENDAR_INPUT_MESSAGES,
  type CalendarInputMessages,
} from './calendar-input.js';

/**
 * Map a validity-bound precision to the picker's selection granularity
 * (ADR-0005 §5): a `year`-precise bound picks a year, `month`/`season` pick a
 * month, everything else picks an exact day. So the date control always
 * presents at the granularity the bound is stated at.
 */
function granularityForPrecision(p: Precision | undefined): CalendarGranularity {
  switch (p) {
    case 'year':
      return 'year';
    case 'month':
      return 'month';
    case 'season':
      return 'season';
    default:
      return 'day';
  }
}

/**
 * Inverse of {@link granularityForPrecision}: the data-model precision hint a
 * picker granularity stores. `day` is the exact, no-hint level (undefined); the
 * coarser levels map 1:1 to their precision (ADR-0005 §5).
 */
function precisionForGranularity(g: CalendarGranularity): Precision | undefined {
  return g === 'day' ? undefined : g;
}

/** The granularity levels a validity bound offers, fine → coarse. */
const VALIDITY_GRANULARITIES: readonly CalendarGranularity[] = ['day', 'month', 'season', 'year'];

/**
 * `<ValidityEditor>` — surfaces `FactValidity` (canonical-day from/to)
 * as two `<CalendarInput>` chips with explicit open-ended toggles.
 *
 * Semantic mapping (03-information-model.md §6):
 *
 *   - `validity = null`         → "Timeless" — no chip is set.
 *   - `{ from: N }`             → "from canonical day N forever".
 *   - `{ to: N }`               → "from always until canonical day N".
 *   - `{ from: A, to: B }`      → closed range `[A, B]`.
 *
 * The editor never constructs invalid ranges (`from > to`); when the user
 * pushes one bound past the other, the editor clamps and the `onChange`
 * callback receives the clamped value.
 */

export interface ValidityEditorMessages extends CalendarInputMessages {
  /** Label for the "from" bound. */
  fromLabel: string;
  /** Label for the "to" bound. */
  toLabel: string;
  /** Heading announcing the whole editor (used as the group's `aria-label`). */
  heading: string;
  /** Visible chip text when the FROM bound is open-ended ("no start date"). */
  openEndedFrom: string;
  /** Visible chip text when the TO bound is open-ended ("no end date"). */
  openEndedTo: string;
  /** Button text to set the FROM bound open-ended ("always was true"). */
  setOpenEndedFrom: string;
  /** Button text to set the TO bound open-ended ("always will be true"). */
  setOpenEndedTo: string;
  /** Button text to seed a date when the bound is currently open-ended. */
  setDateBound?: string;
  /** Accessible label for the FROM bound's precision selector (ADR-0005 §5). */
  fromPrecisionLabel: string;
  /** Accessible label for the TO bound's precision selector. */
  toPrecisionLabel: string;
  /**
   * Localised option labels for each precision level shown in the selector,
   * FINE → COARSE. `day` is the default (no precision hint = an exact day);
   * the coarser levels mark a bound as honestly fuzzy (ADR-0005 §5).
   */
  precisionOptions: {
    day: string;
    month: string;
    season: string;
    year: string;
  };
  /**
   * Open-ended label for the FROM bound (no start date) — shown as the field's
   * value when open, AND as the picker's leading "none" segment. Picking it
   * clears the bound. Reads as the start of the range ("Always → …").
   */
  noneFromLabel: string;
  /** Open-ended label for the TO bound (no end date) — e.g. "Forever". */
  noneToLabel: string;
  /**
   * @deprecated Use `openEndedFrom` / `openEndedTo` for per-bound copy.
   * Kept on the interface so older hosts compile; the editor falls
   * back to the per-bound variants when those are unset.
   */
  openEnded?: string;
  /**
   * @deprecated Use `setOpenEndedFrom` / `setOpenEndedTo` for per-bound copy.
   * Kept on the interface so older hosts compile.
   */
  setOpenEnded?: string;
}

export const DEFAULT_VALIDITY_EDITOR_MESSAGES: ValidityEditorMessages = {
  ...DEFAULT_CALENDAR_INPUT_MESSAGES,
  fromLabel: 'From',
  toLabel: 'Until',
  heading: 'Validity range',
  openEndedFrom: 'No start date',
  openEndedTo: 'No end date',
  setOpenEndedFrom: 'Set to open-ended (no start date)',
  setOpenEndedTo: 'Set to open-ended (no end date)',
  setDateBound: 'Set a date',
  fromPrecisionLabel: 'Start date precision',
  toPrecisionLabel: 'End date precision',
  precisionOptions: {
    day: 'Day (exact)',
    month: 'Month',
    season: 'Season',
    year: 'Year',
  },
  noneFromLabel: 'Always',
  noneToLabel: 'Forever',
};

/**
 * The precision levels the validity picker offers, FINE → COARSE. `day` is the
 * default — it carries no precision hint (an exact day); the coarser levels
 * (`month`/`season`/`year`) mark a bound as honestly fuzzy (ADR-0005 §5).
 * Time-of-day precisions are omitted — a `game_date`-shaped validity bound
 * stores no seconds, so nothing finer than a day is selectable here.
 */
type ValidityPrecisionChoice = 'day' | 'month' | 'season' | 'year';
const PRECISION_CHOICES: readonly ValidityPrecisionChoice[] = [
  'day',
  'month',
  'season',
  'year',
];

export interface ValidityEditorOptions {
  calendar: Calendar;
  value: FactValidity | null;
  onChange: (next: FactValidity | null) => void;
  /** Stable DOM id prefix; used to suffix each chip's inner ids. */
  id: string;
  mode?: 'edit' | 'read';
  locale?: string;
  messages?: Partial<ValidityEditorMessages>;
  /** Optional `data-testid` prefix to ease E2E testing. */
  testid?: string;
  /**
   * Canonical day a bound seeds to when the user un-open-ends it (the
   * "No start/end date" checkbox is unchecked). Defaults to the
   * current Campaign date so a new bound starts at the in-world time
   * the GM is working in, not the calendar anchor. Falls back
   * to the calendar anchor when null/omitted.
   */
  defaultSeed?: number | null;
  /**
   * Inject the date control for a bound instead of the built-in
   * four-`<select>` `calendarInput`. The host returns its own picker (the
   * calendar-grid `dateField` wired to app state) given the bound, its
   * committed day, an `onChange`, and a stable `id`/`testid`. When omitted
   * the editor falls back to `calendarInput` (gallery / standalone use).
   */
  renderPicker?: (args: {
    bound: 'from' | 'to';
    /** Committed day, or `null` when the bound is open-ended. */
    value: number | null;
    onChange: (next: number | null) => void;
    id: string;
    testid?: string;
    /** Trigger text when the bound is open-ended (the "Always"/"Forever" label). */
    placeholder: string;
    /** Selection granularity for this bound, derived from its precision. */
    granularity: CalendarGranularity;
    /**
     * The granularity levels the injected picker should offer in its own
     * in-place segmented control. When the host wires a grid picker, the
     * editor folds precision INTO it (no external `<select>` is rendered).
     */
    granularities: readonly CalendarGranularity[];
    /**
     * The user switched granularity inside the picker — the editor maps it
     * back to a precision hint on this bound (keeping the exact stored day).
     */
    onGranularity: (granularity: CalendarGranularity) => void;
    /** Label for the picker's leading "none" segment — clears (open-ends) this
     * bound. Localised copy from the editor's messages. */
    granularityClearLabel: string;
    /** The user picked "none" in the picker — open-ends this bound. */
    onGranularityClear: () => void;
  }) => Mountable;
}

interface MaybeRange {
  from: number | undefined;
  to: number | undefined;
  fromPrecision?: Precision | undefined;
  toPrecision?: Precision | undefined;
}

function normalizeValidity(next: MaybeRange): FactValidity | null {
  // Clamp: if both set and from > to, swap (carrying each bound's precision
  // with its day so the hint always travels with the value it describes).
  let from = next.from;
  let to = next.to;
  let fromP = next.fromPrecision;
  let toP = next.toPrecision;
  if (from !== undefined && to !== undefined && from > to) {
    [from, to] = [to, from];
    [fromP, toP] = [toP, fromP];
  }
  if (from === undefined && to === undefined) return null;
  const out: FactValidity = {};
  // Precision is meaningless on an absent bound — drop it (mirrors the
  // FactValiditySchema refinement) so an open-ended bound never carries a
  // dangling hint.
  if (from !== undefined) {
    out.from = from;
    if (fromP !== undefined) out.from_precision = fromP;
  }
  if (to !== undefined) {
    out.to = to;
    if (toP !== undefined) out.to_precision = toP;
  }
  return out;
}

export function validityEditor(options: ValidityEditorOptions): Mountable {
  const { calendar, value, onChange, id, mode = 'edit', locale = 'en', testid } = options;
  const messages: ValidityEditorMessages = {
    ...DEFAULT_VALIDITY_EDITOR_MESSAGES,
    ...options.messages,
  };

  const from = value?.from;
  const to = value?.to;
  const fromPrecision = value?.from_precision;
  const toPrecision = value?.to_precision;

  // Seed for a bound the user just un-open-ends. Prefer the current
  // Campaign date (defaultSeed) so the new bound starts at the in-world
  // time the GM is working in; fall back to the calendar anchor (ADR-0006
  // §2 — where this calendar's origin sits on the shared axis) when no
  // campaign date is set. Always a valid canonical day so the picker
  // engages immediately.
  const seed = ((): number => {
    if (typeof options.defaultSeed === 'number') return options.defaultSeed;
    return calendar.anchor_canonical_day;
  })();

  // Mutations shared by both render paths.
  const commitDayFor =
    (bound: 'from' | 'to', boundPrecision: Precision | undefined) =>
    (next: number | null): void => {
      if (next === null) return;
      const updated: MaybeRange =
        bound === 'from'
          ? { from: next, to, fromPrecision: boundPrecision, toPrecision }
          : { from, to: next, fromPrecision, toPrecision: boundPrecision };
      onChange(normalizeValidity(updated));
    };
  // Switch granularity → store the matching precision (ADR-0005 §5). On an
  // open-ended bound, seed the default day so switching to a level starts
  // picking from the campaign date.
  const setGranularityFor =
    (bound: 'from' | 'to', boundValue: number | undefined) =>
    (g: CalendarGranularity): void => {
      const nextPrecision = precisionForGranularity(g);
      const baseDay = boundValue ?? seed;
      const updated: MaybeRange =
        bound === 'from'
          ? { from: baseDay, to, fromPrecision: nextPrecision, toPrecision }
          : { from, to: baseDay, fromPrecision, toPrecision: nextPrecision };
      onChange(normalizeValidity(updated));
    };
  // Open-end THIS bound (the opposite bound + its precision are preserved).
  const clearBoundFor = (bound: 'from' | 'to') => (): void => {
    const remaining: MaybeRange =
      bound === 'from'
        ? { from: undefined, to, fromPrecision: undefined, toPrecision }
        : { from, to: undefined, fromPrecision, toPrecision: undefined };
    onChange(normalizeValidity(remaining));
  };

  const chip = (
    bound: 'from' | 'to',
    boundValue: number | undefined,
    boundPrecision: Precision | undefined,
    chipLabel: string,
  ): Mountable => {
    const chipTestid = testid !== undefined ? `${testid}-${bound}` : undefined;
    // The open-ended label doubles as the field's value text AND the picker's
    // "none" segment — picking it clears the bound (this single state replaces
    // the old open-ended checkbox). Reads as a range: "Always → Forever".
    const noneLabel = bound === 'from' ? messages.noneFromLabel : messages.noneToLabel;
    const commitDay = commitDayFor(bound, boundPrecision);
    const setGranularity = setGranularityFor(bound, boundValue);
    const clearBound = clearBoundFor(bound);

    // Injected grid picker (real app + interactive gallery): ONE inline control
    // per bound — the field shows the date or the open-ended label, and its
    // folded-in picker carries precision + the "none" clear. No checkbox.
    if (options.renderPicker !== undefined) {
      return div(
        {
          class: `dl-validity-bound dl-validity-bound--${bound}`,
          ...(chipTestid !== undefined ? { 'data-testid': chipTestid } : {}),
        },
        [
          span({ class: 'dl-validity-bound-label' }, [chipLabel]),
          options.renderPicker({
            bound,
            value: boundValue ?? null,
            onChange: commitDay,
            id: `${id}-${bound}`,
            placeholder: noneLabel,
            granularity: granularityForPrecision(boundPrecision),
            granularities: VALIDITY_GRANULARITIES,
            onGranularity: setGranularity,
            granularityClearLabel: noneLabel,
            onGranularityClear: clearBound,
            ...(chipTestid !== undefined ? { testid: `${chipTestid}-input` } : {}),
          }),
        ],
      );
    }

    // Fallback (gallery embedded route / unit specimens): no grid to fold into,
    // so keep the explicit open-ended checkbox + four-`<select>` calendarInput +
    // external precision selector.
    const openEndedLabel =
      bound === 'from'
        ? messages.openEndedFrom ?? messages.openEnded ?? 'Open-ended'
        : messages.openEndedTo ?? messages.openEnded ?? 'Open-ended';
    const isOpenEnded = boundValue === undefined;
    return div(
      {
        class: `dl-validity-chip dl-validity-chip--${bound}`,
        ...(chipTestid !== undefined ? { 'data-testid': chipTestid } : {}),
      },
      [
        div({ class: 'dl-validity-chip-head' }, [
          span({ class: 'dl-validity-chip-label' }, [chipLabel]),
          label({ class: 'dl-validity-openended' }, [
            input({
              type: 'checkbox',
              class: 'dl-validity-openended-box',
              checked: isOpenEnded,
              'aria-label': openEndedLabel,
              ...(chipTestid !== undefined ? { 'data-testid': `${chipTestid}-input-clear` } : {}),
              onChange: (event: Event) => {
                const checked = (event.target as HTMLInputElement).checked;
                if (checked) {
                  clearBound();
                  return;
                }
                onChange(
                  normalizeValidity(
                    bound === 'from'
                      ? { from: seed, to, fromPrecision: undefined, toPrecision }
                      : { from, to: seed, fromPrecision, toPrecision: undefined },
                  ),
                );
              },
            }),
            span({ class: 'dl-validity-openended-label' }, [openEndedLabel]),
          ]),
        ]),
        ...(boundValue !== undefined
          ? [
              calendarInput({
                calendar,
                value: boundValue,
                onChange: commitDay,
                id: `${id}-${bound}`,
                mode,
                locale,
                clearable: false,
                ...(chipTestid !== undefined ? { testid: `${chipTestid}-input` } : {}),
                messages: {
                  era: messages.era,
                  year: messages.year,
                  month: messages.month,
                  day: messages.day,
                },
              }),
              div({ class: `dl-validity-precision dl-validity-precision--${bound}` }, [
                label(
                  { class: 'dl-validity-precision-label', for: `${id}-${bound}-precision` },
                  [bound === 'from' ? messages.fromPrecisionLabel : messages.toPrecisionLabel],
                ),
                selectInput({
                  id: `${id}-${bound}-precision`,
                  value: boundPrecision ?? 'day',
                  size: 'md',
                  ariaLabel:
                    bound === 'from' ? messages.fromPrecisionLabel : messages.toPrecisionLabel,
                  ...(chipTestid !== undefined ? { testid: `${chipTestid}-precision` } : {}),
                  options: PRECISION_CHOICES.map((choice) => ({
                    value: choice,
                    label: messages.precisionOptions[choice],
                  })),
                  onChange: (raw) => setGranularity(raw as CalendarGranularity),
                }),
              ]),
            ]
          : []),
      ],
    );
  };

  const inline = options.renderPicker !== undefined;
  return div(
    {
      class: `dl-validity-editor${inline ? ' dl-validity-editor--inline' : ''}`,
      role: 'group',
      'aria-label': messages.heading,
      ...(testid !== undefined ? { 'data-testid': testid } : {}),
    },
    [
      chip('from', from, fromPrecision, messages.fromLabel),
      // Arrow connecting the two bounds as a range (from → until). aria-hidden
      // — the from/until labels convey direction to assistive tech.
      span({ class: 'dl-validity-arrow', 'aria-hidden': 'true' }, ['→']),
      chip('to', to, toPrecision, messages.toLabel),
    ],
  );
}
